import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Stethoscope, CheckCircle, Users, Eye, EyeOff, Edit2, Save, X, Mail, Phone, MapPin, Award, Calendar, TrendingUp } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

export default function ProfessionalProfile() {
  const [user, setUser] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const queryClient = useQueryClient();

  useEffect(() => {
    const loadUser = async () => {
      try {
        const currentUser = await base44.auth.me();
        setUser(currentUser);
        setEditForm({
          professional_bio: currentUser.professional_bio || '',
          professional_specialty: currentUser.professional_specialty || '',
          phone: currentUser.phone || '',
          location: currentUser.location || '',
          accepting_patients: currentUser.accepting_patients ?? true,
          profile_visible: currentUser.profile_visible ?? true,
        });
      } catch (error) {
        console.error('Error loading user:', error);
      }
    };
    loadUser();
  }, []);

  const { data: relations = [] } = useQuery({
    queryKey: ['professional-relations', user?.id],
    queryFn: () => base44.entities.UserProfessionalRelation.filter({ 
      professional_id: user?.id,
      status: 'active'
    }),
    enabled: !!user?.id,
  });

  const { data: patients = [] } = useQuery({
    queryKey: ['professional-patients', user?.id],
    queryFn: async () => {
      const patientIds = relations.map(r => r.user_id);
      if (patientIds.length === 0) return [];
      const allUsers = await base44.entities.User.list();
      return allUsers.filter(u => patientIds.includes(u.id));
    },
    enabled: relations.length > 0,
  });

  const updateProfileMutation = useMutation({
    mutationFn: (data) => base44.auth.updateMe(data),
    onSuccess: async () => {
      const updatedUser = await base44.auth.me();
      setUser(updatedUser);
      setIsEditing(false);
      toast.success('Perfil actualizado');
    },
  });

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500">Cargando...</p>
      </div>
    );
  }

  if (!user.is_professional) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="p-8 text-center max-w-md">
          <Stethoscope className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Acceso Restringido</h2>
          <p className="text-gray-600">Esta sección es solo para profesionales de la salud</p>
        </Card>
      </div>
    );
  }

  if (!user.professional_verified) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="p-8 text-center max-w-md">
          <Award className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Verificación Pendiente</h2>
          <p className="text-gray-600 mb-4">
            Tu perfil profesional está en proceso de verificación. 
            Serás notificado cuando esté aprobado.
          </p>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-left">
            <p className="font-medium text-amber-900 mb-2">Información proporcionada:</p>
            <ul className="space-y-1 text-amber-700">
              <li>• Tipo: {user.professional_type}</li>
              {user.professional_license && <li>• Matrícula: {user.professional_license}</li>}
              {user.professional_specialty && <li>• Especialidad: {user.professional_specialty}</li>}
            </ul>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 via-white to-gray-50 pb-20">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-500/20">
                <Stethoscope className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-gray-900 text-lg">Perfil Profesional</h1>
                <p className="text-xs text-gray-400">Gestiona tu información</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="text-sm font-medium text-green-600">Verificado</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-4 h-4 text-blue-600" />
              <p className="text-sm text-gray-500">Pacientes</p>
            </div>
            <p className="text-2xl font-bold text-gray-900">{relations.length}</p>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 text-green-600" />
              <p className="text-sm text-gray-500">Este mes</p>
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {relations.filter(r => {
                const date = new Date(r.created_date);
                const now = new Date();
                return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
              }).length}
            </p>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-2 mb-2">
              {user.profile_visible ? <Eye className="w-4 h-4 text-indigo-600" /> : <EyeOff className="w-4 h-4 text-gray-400" />}
              <p className="text-sm text-gray-500">Visibilidad</p>
            </div>
            <p className="text-sm font-medium text-gray-900">
              {user.profile_visible ? 'Visible' : 'Oculto'}
            </p>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="w-4 h-4 text-purple-600" />
              <p className="text-sm text-gray-500">Miembro desde</p>
            </div>
            <p className="text-sm font-medium text-gray-900">
              {format(new Date(user.created_date), 'MMM yyyy', { locale: es })}
            </p>
          </Card>
        </div>

        {/* Professional Information */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-900">Información Profesional</h2>
            {!isEditing ? (
              <Button onClick={() => setIsEditing(true)} variant="outline" size="sm">
                <Edit2 className="w-4 h-4 mr-2" />
                Editar
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button onClick={() => setIsEditing(false)} variant="outline" size="sm">
                  <X className="w-4 h-4 mr-2" />
                  Cancelar
                </Button>
                <Button 
                  onClick={() => updateProfileMutation.mutate(editForm)}
                  size="sm"
                  disabled={updateProfileMutation.isPending}
                >
                  <Save className="w-4 h-4 mr-2" />
                  Guardar
                </Button>
              </div>
            )}
          </div>

          <div className="flex items-start gap-6 mb-6">
            {user.profile_picture ? (
              <img 
                src={user.profile_picture} 
                alt={user.full_name}
                className="w-24 h-24 rounded-2xl object-cover border-2 border-blue-100"
              />
            ) : (
              <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                <span className="text-3xl font-bold text-white">
                  {user.full_name?.[0]?.toUpperCase()}
                </span>
              </div>
            )}
            <div className="flex-1">
              <h3 className="text-xl font-bold text-gray-900 mb-1">{user.full_name}</h3>
              <div className="flex flex-wrap gap-2 mb-3">
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 text-sm rounded-full font-medium">
                  <Stethoscope className="w-3 h-3" />
                  {user.professional_type}
                </span>
                {user.professional_license && (
                  <span className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full">
                    MP: {user.professional_license}
                  </span>
                )}
                {user.professional_specialty && (
                  <span className="px-3 py-1 bg-purple-100 text-purple-700 text-sm rounded-full">
                    {user.professional_specialty}
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-500">{user.email}</p>
            </div>
          </div>

          {isEditing ? (
            <div className="space-y-4 pt-4 border-t">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Especialidad</label>
                <Input
                  value={editForm.professional_specialty}
                  onChange={(e) => setEditForm({ ...editForm, professional_specialty: e.target.value })}
                  placeholder="Ej: Nutrición Deportiva, Cardiología..."
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Biografía Profesional</label>
                <textarea
                  value={editForm.professional_bio}
                  onChange={(e) => setEditForm({ ...editForm, professional_bio: e.target.value })}
                  placeholder="Describe tu experiencia, formación y área de especialización..."
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg min-h-[100px] text-sm"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Teléfono</label>
                <Input
                  value={editForm.phone}
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                  placeholder="+54 11 1234-5678"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Ubicación</label>
                <Input
                  value={editForm.location}
                  onChange={(e) => setEditForm({ ...editForm, location: e.target.value })}
                  placeholder="Buenos Aires, Argentina"
                />
              </div>
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">Aceptando nuevos pacientes</p>
                  <p className="text-sm text-gray-500">Los usuarios podrán seleccionarte</p>
                </div>
                <Switch
                  checked={editForm.accepting_patients}
                  onCheckedChange={(checked) => setEditForm({ ...editForm, accepting_patients: checked })}
                />
              </div>
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">Perfil visible</p>
                  <p className="text-sm text-gray-500">Mostrar tu perfil en búsquedas</p>
                </div>
                <Switch
                  checked={editForm.profile_visible}
                  onCheckedChange={(checked) => setEditForm({ ...editForm, profile_visible: checked })}
                />
              </div>
            </div>
          ) : (
            <div className="space-y-4 pt-4 border-t">
              {user.professional_bio && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-1">Biografía</p>
                  <p className="text-sm text-gray-600">{user.professional_bio}</p>
                </div>
              )}
              {user.phone && (
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-gray-400" />
                  <p className="text-sm text-gray-600">{user.phone}</p>
                </div>
              )}
              {user.location && (
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  <p className="text-sm text-gray-600">{user.location}</p>
                </div>
              )}
              <div className="grid md:grid-cols-2 gap-3 pt-2">
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm text-gray-700">Aceptando pacientes</span>
                  <span className={`text-sm font-medium ${user.accepting_patients ? 'text-green-600' : 'text-gray-400'}`}>
                    {user.accepting_patients ? 'Sí' : 'No'}
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm text-gray-700">Visibilidad</span>
                  <span className={`text-sm font-medium ${user.profile_visible ? 'text-green-600' : 'text-gray-400'}`}>
                    {user.profile_visible ? 'Visible' : 'Oculto'}
                  </span>
                </div>
              </div>
            </div>
          )}
        </Card>

        {/* Patients List */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Pacientes Activos</h2>
          {patients.length === 0 ? (
            <div className="text-center py-8">
              <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Aún no tienes pacientes asignados</p>
            </div>
          ) : (
            <div className="space-y-3">
              {patients.map((patient) => {
                const relation = relations.find(r => r.user_id === patient.id);
                return (
                  <motion.div
                    key={patient.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    {patient.profile_picture ? (
                      <img 
                        src={patient.profile_picture} 
                        alt={patient.full_name}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                        <span className="text-lg font-bold text-white">
                          {patient.full_name?.[0]?.toUpperCase()}
                        </span>
                      </div>
                    )}
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{patient.full_name}</p>
                      <p className="text-sm text-gray-500">{patient.email}</p>
                      <div className="flex gap-2 mt-1 text-xs">
                        {relation?.share_products && (
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">
                            Productos
                          </span>
                        )}
                        {relation?.share_health_data && (
                          <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full">
                            Datos de salud
                          </span>
                        )}
                      </div>
                    </div>
                    <span className="text-xs text-gray-400">
                      {format(new Date(relation.created_date), "d MMM yyyy", { locale: es })}
                    </span>
                  </motion.div>
                );
              })}
            </div>
          )}
        </Card>
      </main>
    </div>
  );
}